import React, { useState, useMemo } from 'react';
import { Plus, X } from 'lucide-react';

/**
 * ToolsInput — input "Outils maîtrisés" pour le formulaire de qualification candidat.
 *
 * UX :
 *   - Chips suggérées (cliquer = ajouter)
 *   - Input libre (Enter ou virgule = ajouter)
 *   - Liste des outils sélectionnés affichée comme chips (X pour retirer)
 *   - Suggestions adaptées au profile_type
 *
 * Sortie : array de strings (canoniques quand possible)
 */

const SUGGESTIONS_BY_PROFILE = {
  ai_automation: ['n8n', 'Make', 'Zapier', 'Claude', 'ChatGPT', 'Airtable', 'Notion', 'Bubble', 'OpenAI API'],
  community_manager: ['Canva', 'CapCut', 'Meta Business Suite', 'Notion', 'Buffer', 'Hootsuite', 'Figma'],
  media_buyer: ['Meta Ads', 'Google Ads', 'TikTok Ads', 'Looker Studio', 'Hyros', 'Triple Whale', 'Klaviyo'],
  funnel_builder: ['Systeme.io', 'ClickFunnels', 'GoHighLevel', 'Webflow', 'Stripe', 'Klaviyo', 'Make'],
  developpeur_web: ['React', 'Next.js', 'Node.js', 'Python', 'WordPress', 'Shopify', 'Git', 'Docker', 'Vercel'],
  monteur_video: ['CapCut', 'Premiere Pro', 'DaVinci Resolve', 'After Effects', 'Photoshop'],
  graphiste: ['Figma', 'Photoshop', 'Illustrator', 'Canva', 'After Effects', 'Adobe Suite'],
  copywriter: ['ChatGPT', 'Claude', 'Notion', 'Google Docs', 'WordPress', 'Webflow'],
  assistant_virtuel: ['Notion', 'Google Workspace', 'Slack', 'Trello', 'ClickUp', 'Asana', 'Zoom', 'Calendly'],
  support_client: ['Zendesk', 'Intercom', 'HubSpot', 'Slack', 'Notion', 'Google Workspace'],
  recruteur_rh: ['LinkedIn Recruiter', 'Notion', 'Airtable', 'Zoom', 'Calendly'],
  comptabilite_gestion: ['Excel', 'Sage', 'QuickBooks', 'Pennylane', 'Notion'],
  default: ['Notion', 'ChatGPT', 'Claude', 'Google Workspace', 'Slack', 'Canva', 'Figma', 'Trello', 'Zapier'],
};

const ToolsInput = ({ value = [], onChange, profileType = 'default' }) => {
  const [draft, setDraft] = useState('');

  const suggestions = useMemo(() => {
    const baseKey = (profileType || 'default').toLowerCase();
    const list = SUGGESTIONS_BY_PROFILE[baseKey] || SUGGESTIONS_BY_PROFILE.default;
    // Hide already-selected
    return list.filter(
      (s) => !(value || []).some((v) => v.toLowerCase() === s.toLowerCase())
    );
  }, [profileType, value]);

  const addTool = (tool) => {
    const t = tool.trim();
    if (!t) return;
    const exists = (value || []).some((v) => v.toLowerCase() === t.toLowerCase());
    if (exists) return;
    onChange?.([...(value || []), t]);
  };

  const removeTool = (tool) => {
    onChange?.((value || []).filter((v) => v !== tool));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (draft.trim()) {
        addTool(draft);
        setDraft('');
      }
    } else if (e.key === 'Backspace' && !draft && (value || []).length > 0) {
      removeTool(value[value.length - 1]);
    }
  };

  const handleBlur = () => {
    if (draft.trim()) {
      addTool(draft);
      setDraft('');
    }
  };

  return (
    <div className="space-y-3" data-testid="tools-input">
      {/* Selected chips + inline input */}
      <div className="min-h-[48px] flex flex-wrap gap-2 items-center bg-white border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-[#4C8F6E] focus-within:border-[#4C8F6E]">
        {(value || []).map((tool) => (
          <span
            key={tool}
            className="inline-flex items-center gap-1 bg-[#4C8F6E]/10 text-[#4C8F6E] border border-[#4C8F6E]/30 px-2.5 py-1 rounded-full text-sm font-medium"
            data-testid={`tool-chip-${tool}`}
          >
            {tool}
            <button
              type="button"
              onClick={() => removeTool(tool)}
              className="hover:text-red-600 transition-colors"
              aria-label={`Retirer ${tool}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={(value || []).length === 0 ? 'Tapez un outil puis Entrée (ex: n8n, Notion, Claude…)' : 'Ajouter un autre outil…'}
          className="flex-1 min-w-[180px] outline-none text-sm bg-transparent"
          data-testid="tool-draft-input"
        />
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1.5">Suggestions :</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.slice(0, 12).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addTool(s)}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-gray-300 text-gray-700 hover:border-[#4C8F6E] hover:bg-[#4C8F6E]/5 hover:text-[#4C8F6E] transition-colors"
                data-testid={`tool-suggestion-${s}`}
              >
                <Plus className="h-3 w-3" />
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-400">
        Ajoutez tous les outils, plateformes et logiciels que vous maîtrisez réellement. Un outil mal maîtrisé décrédibilise votre profil.
      </p>
    </div>
  );
};

export default ToolsInput;
